import {useTranslation} from "react-i18next";
import {useLocation, useNavigate} from "react-router-dom";
import {ArrowUturnLeftIcon, CheckCircleIcon} from "@heroicons/react/24/solid";
import {useStateContext} from "../../contexts/ContextProvider";
import {FacilitySwitcherItem} from "../../api/superAdmin/facilitySwitcher/useFacilitySwitcherSearch";
import FacilitySearchModal, {FacilityIdentity} from "./FacilitySearchModal";
import {useSwitchToFacility} from "./useSwitchToFacility";

interface SuperAdminFacilitySwitcherModalProps {
    open: boolean;
    setOpen: (value: boolean) => void;
}

/**
 * Sélecteur d'établissement super admin : consulter n'importe quel hôtel ou
 * groupe SANS usurper un compte (le jeton reste celui du super admin, seul
 * l'en-tête X-Facility-Id change).
 */
export default function SuperAdminFacilitySwitcherModal({open, setOpen}: SuperAdminFacilitySwitcherModalProps) {
    const {t} = useTranslation('navigation');
    const navigate = useNavigate();
    const {pathname} = useLocation();
    const {user, currentFacility} = useStateContext();
    const {switchTo, pendingId} = useSwitchToFacility();
    const isViewingOther = Boolean(currentFacility && currentFacility.id !== user.facility_id);

    const handleSelect = async (facilityId: number, close: () => void) => {
        if (facilityId === currentFacility?.id) {
            close();
            return;
        }

        if (await switchTo(facilityId)) {
            close();
            // Le back-office (CRM, audit…) ne dépend pas de l'établissement : on y reste.
            if (!pathname.startsWith('/super-admin')) {
                navigate('/dashboard');
            }
        }
    };

    return (
        <FacilitySearchModal
            open={open}
            setOpen={setOpen}
            title={t('facility_switcher.title')}
            placeholder={t('facility_switcher.super_admin_search_placeholder')}
            size="md"
            listboxId="super-admin-facility-switcher-results"
            keyboardHint={t('facility_switcher.keyboard_hint')}
            onEnter={(facility, close) => {
                // Une bascule est déjà en cours : on ne l'empile pas.
                if (pendingId === null) handleSelect(facility.id, close);
            }}
            beforeResults={(close) => isViewingOther && user.facility ? (
                <button
                    type="button"
                    disabled={pendingId !== null}
                    onClick={() => handleSelect(user.facility_id, close)}
                    className="w-full mb-3 flex items-center gap-3 px-3 py-2.5 rounded-lg border border-dashed border-main-color text-main-color hover:bg-main-color/5 disabled:opacity-60"
                >
                    <ArrowUturnLeftIcon className="h-4 w-4 flex-shrink-0"/>
                    <span className="text-sm font-medium truncate">
                        {t('facility_switcher.back_to_mine', {name: user.facility.name})}
                    </span>
                </button>
            ) : null}
            renderRow={(facility, close) => (
                <SwitcherRow
                    facility={facility}
                    isActive={currentFacility?.id === facility.id}
                    isPending={pendingId === facility.id}
                    disabled={pendingId !== null}
                    onSelect={(id) => handleSelect(id, close)}
                />
            )}
        />
    );
}

interface SwitcherRowProps {
    facility: FacilitySwitcherItem;
    isActive: boolean;
    isPending: boolean;
    disabled: boolean;
    onSelect: (facilityId: number) => void;
}

function SwitcherRow({facility, isActive, isPending, disabled, onSelect}: SwitcherRowProps) {
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={() => onSelect(facility.id)}
            className="w-full text-left px-3 py-2.5 rounded-lg transition-colors duration-150 flex items-center gap-3 hover:bg-gray-50 disabled:opacity-60"
        >
            <FacilityIdentity facility={facility} isActive={isActive}/>

            {isPending ? (
                <span className="h-4 w-4 flex-shrink-0 rounded-full border-2 border-main-color border-t-transparent animate-spin"/>
            ) : isActive ? (
                <CheckCircleIcon className="h-5 w-5 text-main-color flex-shrink-0"/>
            ) : null}
        </button>
    );
}
